import * as zod from 'zod';

import { anchorHasNoTarget } from '@sb/ai/types';
import type { DemonstrationAnchor } from '@sb/ai/types';
import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';
import {
  assertNotExamplePlaceholder,
  SKILL_PLACEHOLDER_MESSAGE,
} from '../../util/examplePlaceholder';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

type RunDemonstrationArgs = Extract<
  RcStep.Arguments,
  { argumentKind: 'RunDemonstration' }
>;

/**
 * The row's anchor narrowed by `kind` alone. `SkillData.shared` is an untyped
 * record, so the kind is checked against the discriminator before
 * `anchorHasNoTarget` — whose `default:` branch throws — ever sees it. The
 * target fields are passed through as they arrive, exactly as
 * `RunDemonstrationStepView` and `SkillDetailParametersTab` read them, so all
 * three agree on what counts as a target.
 *
 * An absent anchor, and a kind outside the discriminator (a row stripped of
 * it, or a future fourth kind), both come back undefined and read as
 * unanchored — matching `resolveSkill`'s `fallbackTo: 'robot-space'`. A
 * validation rule must not block Play over a shape it cannot name to the
 * operator.
 */
function toKnownAnchor(anchor: unknown): DemonstrationAnchor | undefined {
  const kind = (anchor as { kind?: unknown } | null | undefined)?.kind;

  return kind === 'robot-space' || kind === 'locate-skill' || kind === 'pose'
    ? (anchor as DemonstrationAnchor)
    : undefined;
}

const NO_SHOWPATH_SKILL_MESSAGE = 'No ShowPath skill selected';

// `SkillData.shared` is an untyped record; only the recording list is read.
const ShowPathRecordings = zod.object({
  recordings: zod.array(zod.unknown()).optional().catch(undefined),
});

/**
 * A RunDemonstration step replays a saved demonstration, so it needs one
 * selected, and a selected ShowPath skill needs at least one recording. When
 * repositioning is on it also needs the position list it repositions to;
 * without it the step has nothing to anchor the replay.
 *
 * It also refuses a skill whose path anchor names a kind with no target, but
 * only on the one step shape that re-grounds the replay on the recording's
 * baked-in reference frame. The replay path never reads `shared.anchor` — the
 * reference is baked into the recording at record time — so the anchor can
 * only mislead where that baked frame is consumed: such a step re-grounds on
 * whatever reference its last take happened to capture, under an anchor the
 * skill no longer describes.
 */
class RunDemonstrationRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.RunDemonstration', 'RunDemonstration');
  }

  protected validate({ args, ctx, warn }: CanonicalValidateArgs): void {
    const a = args as RunDemonstrationArgs;

    if (!a.skillId && !a.recordingId) {
      const skillsEnabled = ctx?.featureFlags?.enableSkills;

      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: skillsEnabled
          ? NO_SHOWPATH_SKILL_MESSAGE
          : 'Select a demonstration to replay.',
        fieldId: skillsEnabled ? 'skillId' : 'recordingId',
      });
    }

    if (a.skillId) {
      // Before the lookup, which skips a skill it cannot find.
      assertNotExamplePlaceholder(
        a.skillId,
        'skillId',
        SKILL_PLACEHOLDER_MESSAGE,
      );
    }

    if (a.repositionEnabled && !a.positionListID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Select a position to reposition the demonstration to.',
        fieldId: 'positionListID',
      });
    }

    if (!a.skillId) return;

    const skill = this.resolveSkill(
      ctx,
      a.skillId,
      'ai-motion',
      NO_SHOWPATH_SKILL_MESSAGE,
    );

    if (!skill) return;

    warn(this.draftSkillWarning(skill));

    // The runner replays the newest recording and refuses a skill whose last
    // entry is not a recording id (`resolveSkillRecording`).
    const { recordings } = ShowPathRecordings.parse(skill.shared ?? {});

    if (typeof recordings?.at(-1) !== 'string') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Demonstrate a ShowPath recording to run this skill',
        fieldId: 'skillId',
      });
    }

    // `repositionEnabled` + `pin: 'reference'` is the only shape that consumes
    // the recording's reference frame: `computeMoveToStart` shifts the replay
    // by targetPose · inv(refWorldPose) under that pin and ignores
    // `refWorldPose` under every other, and reposition off replays in the
    // recorded world frame with `target`/`pin` ignored outright
    // (`libs/routine-runner/Step/RunDemonstration/Arguments.ts`). Anywhere
    // else a half-configured anchor cannot move the arm, and blocking Play
    // over it would refuse a step whose motion is unchanged by the field.
    if (!a.repositionEnabled || a.pin !== 'reference') return;

    const { anchor } = (skill.shared ?? {}) as { anchor?: unknown };

    const known = toKnownAnchor(anchor);

    // Completeness comes from `anchorHasNoTarget` rather than a local check,
    // so there is one place to edit when what counts as a target changes. It
    // is not the predicate readiness uses — the contract registry reports the
    // slot's forms with `isPresent` (`libs/ai/contracts/instance.ts`) — and
    // the two are only required to agree in one direction: a row this refuses
    // must never read ready. They agree on every value the schema admits
    // today, and `routineValidationOracle.test.ts` is what fails if an edit to
    // either side breaks it. A trimming check here would: `'   '` is
    // `isPresent`-filled and writable via `AgentWritableAnchor`'s `min(1)`.
    if (!known || !anchorHasNoTarget(known)) return;

    // 'robot-space' anchors nothing by design, so `anchorHasNoTarget` is false
    // for it and only the two targetable kinds reach here.
    const message =
      known.kind === 'locate-skill'
        ? 'This ShowPath skill is anchored to a Locate skill, but none is ' +
          'selected. Choose one before the routine can run.'
        : 'This ShowPath skill is anchored to a pose, but none is selected. ' +
          'Choose one before the routine can run.';

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message,
      fieldId: 'skillId',
    });
  }
}

export const RunDemonstrationValidation: ValidationRule =
  new RunDemonstrationRule().toValidationRule();
